import { useRef, useState } from 'react';
import { sam } from '../../../DataAccessLayer/services';
import { postData } from '../../../DataAccessLayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faFileCsv, faTimes } from '@fortawesome/pro-regular-svg-icons';
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { ERROR_CODE } from '../../common/constants';

export const UploadCard = ({ isUploadAllowed }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef();

  const removeFileUpload = () => {
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a “.csv” file.');
      return;
    }

    if (file.size === 0) {
      toast.error('The CSV is empty.');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await postData(sam.advisorRules().upload, formData, true);

      toast.success('Successfully processing advisor rules update.');
    } catch (err) {
      toast.error(
        `Unable to process advisor rules update${err?.status === ERROR_CODE.BAD_REQUEST ? `: ${err?.data?.message}.` : '.'}`,
      );
    } finally {
      setIsUploading(false);
      removeFileUpload();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] ?? null;
    setFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    setFile(file);
  };

  return (
    <Card elevation={0}>
      <Typography component="h3" align="center">
        Update Advisor Rules for Unassigned Students
      </Typography>
      <CardContent
        onDragOver={isUploadAllowed ? onDragOver : undefined}
        onDragLeave={isUploadAllowed ? onDragLeave : undefined}
        onDrop={isUploadAllowed ? onDrop : undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: isDragging ? '2px dashed red' : '2px dashed black',
          p: 2,
          borderRadius: 1,
          transition: '0.2s',
          mt: 2,
        }}
      >
        <input
          ref={fileInputRef}
          accept=".csv"
          type="file"
          style={{
            display: 'none',
          }}
          onChange={handleFileSelect}
        />
        <IconButton
          disabled={isUploading || !isUploadAllowed}
          onClick={() => fileInputRef?.current.click()}
        >
          <FontAwesomeIcon icon={faCloudArrowUp} size="xl" />
        </IconButton>
        <Typography variant="caption" sx={{ mb: 1 }}>
          or drag & drop CSV here
        </Typography>

        {/* Show selected file */}
        {file && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: '100%',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <FontAwesomeIcon icon={faFileCsv} />
              <Typography variant="body2">{file.name}</Typography>
            </Box>

            <IconButton size="small" onClick={removeFileUpload} disabled={isUploading}>
              {isUploading ? (
                <CircularProgress className="circularProgress" />
              ) : (
                <FontAwesomeIcon icon={faTimes} />
              )}
            </IconButton>
          </Box>
        )}

        <Button
          className="myOdu__button primary"
          variant="contained"
          disabled={!file || isUploading || !isUploadAllowed}
          onClick={() => file && handleFileUpload(file)}
          sx={{ mt: 1 }}
        >
          {isUploading ? 'Uploading CSV...' : 'Upload CSV'}
        </Button>
      </CardContent>
    </Card>
  );
};
